import CardsHelper from '../../../Utility/CardsHelper';
import BaseModifierBehavior from './BaseModifierBehavior';
import { CardType } from '../../../Prototype/Level/Card/CardType';
import { Random } from '../../../Utility/Random';
import CardStateHelper from '../CardStateHelper';
import { CardTask } from '../CardTasks';
import {
    DEFAULT_CARD_HEIGHT,
    DEFAULT_CARD_WIDTH,
} from '../../../Prototype/Level/Card/ICardData';
import { ICardsState, ICardStateData } from '../ICardState';
import { ICardModifierData } from '../../../Prototype/Level/Card/ICardModifierData';
import { initialCardStateData } from '../initialCardStateData';
import { IGameState } from 'State/Game/IGameState';

export default class BombModifierBehavior extends BaseModifierBehavior {
    public onCardMatched(
        state: IGameState,
        matchedCardID: string
    ): IGameState {
        let cardsState = CardStateHelper.getCardsState(state);
        if (!this.validateUpdate(cardsState)) return state;

        if (matchedCardID === this.cardID) return state;

        cardsState = this.updateModifier(cardsState, this.cardID, {
            properties: {
                ...this.modifier.properties,
                timer: this.modifier.properties.timer - 1,
            },
        });

        if (this.modifier.properties.timer <= 0) {
            // Discard this card.
            cardsState = CardStateHelper.setCardDiscarded(cardsState, this.cardID);
        }

        return CardStateHelper.updateGameState(state, cardsState);
    }

    public onCardMatchedLate(state: ICardsState): ICardsState {
        if (this.modifier.properties.exploded) return state;

        if (state.discarded.indexOf(this.cardID) === -1) return state;

        if (this.modifier.properties.timer <= 0) {
            return this.explode(state, this.cardID);
        }

        return state;
    }

    public onUndoUsed(state: ICardsState, prevCardState: ICardStateData, prevModifier: ICardModifierData): ICardsState {
        if (!this.validateUpdate(state)) return state;

        state = this.updateModifier(state, this.cardID, {
            properties: {
                ...this.modifier.properties,
                timer: prevModifier.properties.timer,
            },
        });

        return state;
    }

    private explode(cardsState: ICardsState, cardID: string): ICardsState {
        const newCards = [];

        // Add 3-5 random cards positions on top of other in-play cards.
        const numberOfCardsAdded = Random.int(3, 5);
        for (let i = 0; i < numberOfCardsAdded; i++) {
            // Default new card data (in case a target card to overlay is not found).
            const newCardData = {
                x: Random.value(-500, 500),
                y: Random.value(200, 500),
                rotation: 0,
                depth: i,
            };

            // Attempt to find a random, face-up, not obscured, in-play card and add a new card in front.
            const targetCards = cardsState.inPlay.filter((cardID: string) => {
                const behavior = CardStateHelper.getCardBehavior(
                    cardsState,
                    cardID
                );
                return (
                    behavior.isFaceUp(cardsState) &&
                    !behavior.isObscured(cardsState)
                );
            });

            const targetCardID =
                targetCards[Random.int(0, targetCards.length - 1)];

            // If a target card is found set relative position/rotation/depth.
            if (targetCardID) {
                const cardData = cardsState.cards[targetCardID];
                newCardData.x = cardData.x;
                newCardData.y = cardData.y; // + 25;
                newCardData.rotation =
                    cardData.rotation + Random.value(-Math.PI / 8, Math.PI / 8);
                newCardData.depth = cardData.depth + 1;

                cardsState = {
                    ...cardsState,
                    cards: {
                        ...cardsState.cards,
                        [targetCardID]: {
                            ...cardsState.cards[targetCardID],
                            faceUp: false,
                        },
                    },
                };

                if (i === 0) {
                    cardsState = CardStateHelper.setStateTaskData(cardsState, {
                        task: CardTask.FLIP,
                        cards: [targetCardID],
                        faceUp: false
                    });
                }
            }

            const newCardID = `card_${Random.ID()}`;

            cardsState = CardStateHelper.addCard(cardsState, {
                ...initialCardStateData,
                id: newCardID,
                type: CardType.value,
                random: true,
                value: CardsHelper.instance.draw(),
                faceUp: true,
                sequence: 0,
                modifiers: [],
                attempts: 0,
                x: newCardData.x,
                y: newCardData.y,
                rotation: newCardData.rotation,
                depth: newCardData.depth,
                width: DEFAULT_CARD_WIDTH,
                height: DEFAULT_CARD_HEIGHT,
            });

            newCards.push(newCardID);

            cardsState = CardStateHelper.setCardInPlay(cardsState, newCardID);
        }

        // Set action data to trigger bomb explode task.
        cardsState = CardStateHelper.setStateTaskData(cardsState, {
            task: CardTask.BOMB,
            cards: [cardID, ...newCards],
        });

        // Clear undo history when a bomb explodes.
        cardsState = CardStateHelper.clearUndoHistory(cardsState);

        cardsState = this.updateModifier(cardsState, this.cardID, {
            properties: {
                ...this.modifier.properties,
                exploded: true,
            },
        });

        cardsState = CardStateHelper.checkCardsObscured(cardsState);

        return cardsState;
    }
}
